import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StatsSample, StreamFrame, World } from '../../types';
import { StreamStatus, useWorldStream } from '../../hooks/useWorldStream';
import { Icon } from '../ui/Icon';
import { LineChart } from '../ui/LineChart';
import { Empty, Loading, Note, Section } from '../ui/primitives';
import { fmtBytes, fmtRate, statusInfo } from '../../utils/format';

/** Ten minutes at one sample every two seconds. */
const MAX_SAMPLES = 300;
const SAMPLE_INTERVAL_S = 2;

const STATUS_LABEL: Record<StreamStatus, string> = {
  off: 'Off',
  connecting: 'Connecting to the box…',
  open: 'Live',
  reconnecting: 'Reconnecting…',
  paused: 'Paused (tab hidden)',
  ended: 'Ended',
  error: 'Stopped',
};

const dotClass = (status: StreamStatus) =>
  status === 'open' ? 'ldot ldot--on' : status === 'connecting' || status === 'reconnecting' ? 'ldot ldot--busy' : status === 'error' ? 'ldot ldot--err' : 'ldot';

const pct = (v: number) => `${v.toFixed(v >= 10 ? 0 : 1)}%`;

interface Props {
  world: World;
}

/** Live CPU, memory, network and disk of the world's box and its minecraft container. */
const PerfTab: React.FC<Props> = ({ world }) => {
  const [samples, setSamples] = useState<StatsSample[]>([]);
  const bufRef = useRef<StatsSample[]>([]);
  const frameRef = useRef(false);
  const st = statusInfo(world.status);
  const running = st.active || st.busy;

  const onFrame = useCallback((frame: StreamFrame) => {
    if (frame.type !== 'stats') return;
    const buf = bufRef.current;
    if (buf.length && frame.sample.ts <= buf[buf.length - 1].ts) return; // replayed sample we already have
    buf.push(frame.sample);
    if (buf.length > MAX_SAMPLES) buf.splice(0, buf.length - MAX_SAMPLES);
    if (frameRef.current) return;
    frameRef.current = true;
    window.requestAnimationFrame(() => {
      frameRef.current = false;
      setSamples([...bufRef.current]);
    });
  }, []);

  const { status, error, endReason, retry } = useWorldStream(world.id, 'stats', running, onFrame);

  useEffect(() => {
    bufRef.current = [];
    setSamples([]);
  }, [world.id]);

  const latest = samples.length ? samples[samples.length - 1] : null;
  const hasContainer = samples.some((s) => s.ct != null);
  const times = samples.map((s) => s.ts);
  const spanSeconds = samples.length > 1 ? Math.round((times[times.length - 1] - times[0]) / 1000) : SAMPLE_INTERVAL_S;
  const span = spanSeconds < 90 ? `${spanSeconds} s ago` : `${Math.round(spanSeconds / 60)} min ago`;
  const finished = status === 'ended' || status === 'error';

  const chip = (
    <span className="chip" title={error || undefined}>
      <span className={dotClass(status)} />
      {status === 'ended' && endReason ? `Ended` : STATUS_LABEL[status]}
    </span>
  );

  return (
    <Section
      title="Performance"
      blurb="Resource usage of this world's server, sampled every two seconds while you watch. Host figures cover the whole box; container figures are the Minecraft process."
      wide
      actions={
        <>
          {chip}
          {finished && (
            <button type="button" className="btn btn--sm" onClick={retry}>
              <Icon name="refresh" size={12} /> Reconnect
            </button>
          )}
        </>
      }
    >
      {error && status !== 'open' && <Note tone={status === 'error' ? 'danger' : 'warn'}>{error}</Note>}
      {!running ? (
        <Empty icon="activity" title="The world is offline">
          Start the world to see live metrics.
        </Empty>
      ) : !latest ? (
        <Loading label={status === 'open' ? 'Waiting for the first sample…' : STATUS_LABEL[status]} />
      ) : (
        <>
          <div className="stats">
            <div className="stat">
              <small>CPU</small>
              <b>{pct(latest.host.cpuPct)}</b>
              <i>{latest.ct ? `container ${pct(latest.ct.cpuPct)} · ` : ''}{latest.host.ncpu} cpu · load {latest.host.load1}</i>
            </div>
            <div className="stat">
              <small>Memory</small>
              <b>{fmtBytes(latest.host.memUsed)}</b>
              <i>
                of {fmtBytes(latest.host.memTotal)}
                {latest.ct ? ` · container ${fmtBytes(latest.ct.memUsed)}${latest.ct.memLimit ? ` / ${fmtBytes(latest.ct.memLimit)}` : ''}` : ''}
              </i>
            </div>
            <div className="stat">
              <small>Network</small>
              <b>{fmtRate(latest.host.netRx)}</b>
              <i>in · {fmtRate(latest.host.netTx)} out</i>
            </div>
            <div className="stat">
              <small>Disk</small>
              <b>{fmtBytes(latest.host.diskUsed)}</b>
              <i>
                of {fmtBytes(latest.host.diskTotal)} · {fmtRate(latest.host.diskRead)} read · {fmtRate(latest.host.diskWrite)} write
              </i>
            </div>
          </div>
          {running && !hasContainer && samples.length > 2 && (
            <Note tone="info" className="note--top">
              The container's own usage is not readable on this box, so only host figures are shown.
            </Note>
          )}
          <div className="charts">
            <LineChart
              title="CPU"
              times={times}
              yMax={100}
              format={(v) => `${Math.round(v)}%`}
              spanLabel={span}
              series={[
                { name: 'Host', slot: 'a', values: samples.map((s) => s.host.cpuPct) },
                ...(hasContainer ? [{ name: 'Container', slot: 'b' as const, values: samples.map((s) => (s.ct ? s.ct.cpuPct : null)) }] : []),
              ]}
            />
            <LineChart
              title="Memory"
              times={times}
              yMax={latest.host.memTotal}
              format={fmtBytes}
              spanLabel={span}
              series={[
                { name: 'Host used', slot: 'a', values: samples.map((s) => s.host.memUsed) },
                ...(hasContainer ? [{ name: 'Container', slot: 'b' as const, values: samples.map((s) => (s.ct ? s.ct.memUsed : null)) }] : []),
              ]}
            />
            <LineChart
              title="Network"
              times={times}
              floor={10 * 1024}
              format={fmtRate}
              spanLabel={span}
              series={[
                { name: 'In', slot: 'a', values: samples.map((s) => s.host.netRx) },
                { name: 'Out', slot: 'b', values: samples.map((s) => s.host.netTx) },
              ]}
            />
            <LineChart
              title="Disk I/O"
              times={times}
              floor={10 * 1024}
              format={fmtRate}
              spanLabel={span}
              series={[
                { name: 'Read', slot: 'a', values: samples.map((s) => s.host.diskRead) },
                { name: 'Write', slot: 'b', values: samples.map((s) => s.host.diskWrite) },
              ]}
            />
          </div>
        </>
      )}
    </Section>
  );
};

export default PerfTab;
